import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { pointInPolygon, polygonCentroid } from "@/components/map/LeafletMap";
import Shell from "@/components/Shell";
import PhotoUpload from "@/components/PhotoUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LayoutDashboard, Inbox, Users, MapPin, Tags, DollarSign, Settings, Loader2, Plus, Trash2, Check, X, Eye, EyeOff, Mail, CheckCircle2, Circle, ArrowRight, Clock, CreditCard, Layers, Milestone, QrCode, FileText, TriangleAlert, Store, Route, Building2, Gift, GraduationCap, FolderOpen } from "lucide-react";
import { SetupFeeNote, RevenueShareNote } from "@/components/ShopPricingInfo";
import MyZonesPanel from "@/components/shop/MyZonesPanel";
import LocationPicker from "@/components/shop/LocationPicker";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import ShopPreviewQR from "@/components/shop/ShopPreviewQR";
import ShopReceipt from "@/components/shop/ShopReceipt";
import ShopBillingPanel from "@/components/shop/ShopBillingPanel";
import MarketplacePanel from "@/components/shop/MarketplacePanel";
import ContentManager from "@/components/content/ContentManager";
import ShopRoutesPanel from "@/components/shop/ShopRoutesPanel";
import ShopContentProfile from "@/components/shop/ShopContentProfile";
import ZonePinPreview from "@/components/shop/ZonePinPreview";
import ProspectsPanel from "@/components/admin/ProspectsPanel";
import { FollowUpsDueCard } from "@/components/shop/FollowUpsDueCard";
import PartnerAcademy from "@/components/academy/PartnerAcademy";
import MarketingAssets from "@/components/marketing/MarketingAssets";
import TrailheadsAdminPanel from "@/components/admin/TrailheadsAdminPanel";
import { toast } from "sonner";

function Stat({ icon: Icon, label, value, color = "text-primary" }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <Icon className={`w-5 h-5 ${color} mb-3`} />
      <div className="text-3xl font-heading font-extrabold">{value}</div>
      <div className="text-sm text-muted-foreground">{label}</div>
    </div>
  );
}

export default function ShopDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [prospectsDue, setProspectsDue] = useState(false);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [advertisers, setAdvertisers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [connect, setConnect] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [profile, setProfile] = useState(null);

  const [newCat, setNewCat] = useState("");
  const [zones, setZones] = useState([]);
  const [showRecoup, setShowRecoup] = useState(false);
  const [claimStatus, setClaimStatus] = useState(null);
  const [onboardDoneDismissed, setOnboardDoneDismissed] = useState(false);

  const loadAll = async () => {
    const [s, p, a, c, po, me, mz, cn, fc] = await Promise.all([
      api.get("/shop/stats").catch(() => ({ data: {} })),
      api.get("/shop/listings", { params: { status: "pending" } }).catch(() => ({ data: [] })),
      api.get("/shop/advertisers").catch(() => ({ data: [] })),
      api.get("/categories").catch(() => ({ data: [] })),
      api.get("/shop/payouts").catch(() => ({ data: [] })),
      api.get("/auth/me"),
      api.get("/my/zones").catch(() => ({ data: [] })),
      api.get("/shop/connect/status").catch(() => ({ data: {} })),
      api.get("/shop/founding-claim").catch(() => ({ data: null })),
    ]);
    setStats(s.data); setPending(p.data); setAdvertisers(a.data);
    setCategories(c.data); setPayouts(po.data);
    setProfile(me.data.tenant);
    setZones(mz.data);
    setConnect(cn.data);
    setClaimStatus(fc.data);
    if (s.data.recoup_achieved && me.data.tenant?.recoup_celebrated !== true) {
      setShowRecoup(true);
      try { await api.post("/shop/recoup-celebrated"); } catch (e) {}
    }
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab") === "payouts") setTab("payouts");
    if (params.get("tab") === "billing") setTab("billing");
    if (params.get("tab") === "trailheads") setTab("trailheads");
  }, []);

  const claimState = claimStatus || {};
  const needsClaim = !!(claimState.eligible && !claimState.claim && !claimState.hasZone);
  const claimInReview = !!(claimState.claim && claimState.claim.status && claimState.claim.status !== "fulfilled");

  const onboardingSteps = useMemo(() => {
    if (!stats) return [];
    return [
      ...(claimStatus ? [{ label: claimInReview ? "Claim submitted — awaiting review" : "Claim your complimentary zone", done: !!(claimState.hasZone || claimState.claim?.status === "fulfilled"), route: "/claim-zone", testid: "claim" }] : []),
      { label: "Set up payouts (Stripe)", done: !!connect?.payouts_enabled, target: "payouts" },
      { label: "Upload your logo", done: !!profile?.logo_file_id, target: "settings" },
      { label: "Create your first category", done: stats.categories > 0, target: "categories" },
      { label: "Add your first attraction", done: stats.attractions > 0, target: "content" },
    ];
  }, [stats, profile, claimStatus, connect]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guide the partner: when a step completes in-session, auto-advance to the next unfinished step's tab.
  const prevNextKeyRef = useRef(undefined);
  useEffect(() => {
    if (!onboardingSteps.length) return;
    const next = onboardingSteps.find((s) => !s.done);
    const nextKey = next ? (next.testid || next.target) : "__done__";
    const prev = prevNextKeyRef.current;
    prevNextKeyRef.current = nextKey;
    if (prev === undefined || prev === nextKey) return;
    if (next && !next.route && ["settings", "categories", "content"].includes(tab)) setTab(next.target);
  }, [onboardingSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (id) => { await api.post(`/shop/listings/${id}/approve`); toast.success("Approved — now a Sponsored Pin"); loadAll(); };
  const reject = async (id) => { await api.post(`/shop/listings/${id}/reject`); toast("Listing rejected"); loadAll(); };

  const addCat = async () => {
    if (!newCat.trim()) return;
    try { await api.post("/categories", { name: newCat }); setNewCat(""); loadAll(); toast.success("Category added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delCat = async (id) => { await api.delete(`/categories/${id}`); loadAll(); };

  const startConnectOnboarding = async () => {
    setConnectLoading(true);
    try {
      const { data } = await api.post("/shop/connect/onboarding-link", { origin_url: window.location.origin });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't open Stripe payout setup. Please try again shortly.");
        setConnectLoading(false);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setConnectLoading(false);
    }
  };

  const sendDigestPreview = async () => {
    try {
      const { data } = await api.post("/shop/digest/preview");
      toast.success(`Test digest sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const saveProfile = async () => {    try {
      await api.put("/shop/profile", { name: profile.name, region: profile.region, contact_email: profile.contact_email, logo_file_id: profile.logo_file_id, digest_enabled: profile.digest_enabled !== false, estimated_yearly_riders: profile.estimated_yearly_riders === "" || profile.estimated_yearly_riders == null ? null : parseInt(profile.estimated_yearly_riders), phone: profile.phone ?? "", hours: profile.hours ?? "", address: profile.address ?? "", city: profile.city ?? "", state: profile.state ?? "", zip: profile.zip ?? "", description: profile.description ?? "", booking_url: profile.booking_url ?? "", photos: profile.photos || [], latitude: profile.latitude === "" || profile.latitude == null ? null : parseFloat(profile.latitude), longitude: profile.longitude === "" || profile.longitude == null ? null : parseFloat(profile.longitude) });
      toast.success("Profile updated");
      loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const nav = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "myzones", label: "My Zones", icon: Layers },
    { key: "prospects", label: "Prospects", icon: Building2 },
    { key: "marketplace", label: "Browse Zones", icon: Store },
    { key: "previewqr", label: "Preview QR", icon: QrCode },
    { key: "content", label: "Content", icon: Milestone },
    { key: "routes", label: "Routes", icon: Route },
    { key: "approvals", label: "Approval Queue", icon: Inbox, count: stats?.pending_approvals || 0 },
    { key: "advertisers", label: "Advertisers", icon: Users },
    { key: "categories", label: "Categories", icon: Tags },
    { key: "payouts", label: "Payouts", icon: DollarSign },
    { key: "billing", label: "Lease Billing", icon: CreditCard },
    { key: "receipt", label: "Receipt", icon: FileText },
    { key: "settings", label: "Account Settings", icon: Settings },
    { key: "academy", label: "Partner Academy", icon: GraduationCap },
    { key: "marketing", label: "Marketing Assets", icon: FolderOpen },
    { key: "trailheads", label: "Trailheads & Amenities", icon: MapPin },
    { key: "contentprofile", label: "Public Listing", icon: Store },
  ];

  if (!stats) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (profile && profile.approved === false) {
    return (
      <Shell nav={[]} active="" onNavigate={() => {}} title="Pending Approval" badge="Bike Rental Shop">
        <div className="max-w-xl mx-auto text-center py-20" data-testid="shop-pending-gate">
          <div className="w-16 h-16 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-amber-400" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-3">Your shop is pending approval</h2>
          <p className="text-muted-foreground">Velopaths is reviewing your bike rental shop. We'll email you as soon as it's approved — then you'll be able to set up categories, attractions and start accepting advertisers.</p>
        </div>
      </Shell>
    );
  }

  if (profile && profile.approved && profile.active !== true) {
    return (
      <Shell nav={[]} active="" onNavigate={() => {}} title="Complete Setup" badge="Bike Rental Shop">
        <div className="max-w-xl mx-auto text-center py-20" data-testid="shop-payment-gate">
          <div className="w-16 h-16 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-6">
            <CreditCard className="w-8 h-8 text-primary" />
          </div>
          <h2 className="font-heading text-2xl font-bold mb-3">One last step to activate your shop</h2>
          <p className="text-muted-foreground mb-6">Your shop is approved! Pay the one-time Founding Partner setup fee — which includes your first zone and one year of participation — to activate your partner dashboard, advertiser approvals, and your curated rider map.</p>
          <div className="max-w-md mx-auto mb-6 text-left"><SetupFeeNote fee={99} /></div>
          <Button size="lg" onClick={() => navigate(`/pay/setup?shop=${profile.id}`)} data-testid="go-to-payment-button"><CreditCard className="w-4 h-4 mr-2" />Pay setup fee</Button>
        </div>
      </Shell>
    );
  }

  const ClaimBanner = () => {
    if (needsClaim) return (
      <div data-testid="claim-complimentary-banner" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="w-11 h-11 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0"><Gift className="w-5 h-5 text-emerald-400" /></div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-emerald-300">Claim your complimentary Founding Partner zone</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Your first zone is included free for one year. Pick the territory you'd like to manage — it activates once a Velopaths admin approves your claim.</p>
        </div>
        <Button onClick={() => navigate("/claim-zone")} data-testid="claim-complimentary-cta" className="shrink-0 w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white"><Gift className="w-4 h-4 mr-2" />Choose my free zone</Button>
      </div>
    );
    if (claimInReview) return (
      <div data-testid="claim-review-banner" className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-5 flex flex-col sm:flex-row sm:items-start gap-3">
        <Clock className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-heading font-bold text-amber-300">Your complimentary zone claim is under review</h3>
          <p className="text-sm text-muted-foreground mt-0.5">A Velopaths admin is reviewing {claimState.claim?.zoneName ? <strong className="text-foreground">{claimState.claim.zoneName}</strong> : "your requested zone"}. You'll get an email once it's approved.</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/claim-zone")} data-testid="claim-review-view" className="shrink-0 w-full sm:w-auto">View status</Button>
      </div>
    );
    return null;
  };

  return (
    <Shell nav={nav} active={tab} onNavigate={(t) => { setProspectsDue(false); setTab(t); }} title={profile?.name || "Bike Rental Shop"} badge="Bike Rental Shop" logoId={profile?.logo_file_id} fluid={tab === "prospects"}>
      {tab === "overview" && (
        <div className="space-y-6">
          <ClaimBanner />
          {showRecoup && (
            <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex items-start gap-3" data-testid="recoup-celebration-banner">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-heading font-bold text-emerald-300">Setup fee recouped! 🎉</h3>
                <p className="text-sm text-muted-foreground mt-0.5">Your shop earnings have reached $99 — your one-time Founding Partner setup fee is fully covered. Every additional advertiser is now pure profit.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowRecoup(false)} data-testid="dismiss-recoup-banner" className="text-emerald-300"><X className="w-4 h-4" /></Button>
            </div>
          )}
          {(() => {
            const steps = onboardingSteps;
            const doneCount = steps.filter((s) => s.done).length;
            if (!steps.length) return null;
            if (doneCount === steps.length) {
              const onboardKey = profile?.id ? `vp_onboard_done_${profile.id}` : null;
              if (onboardDoneDismissed || (onboardKey && localStorage.getItem(onboardKey) === "1")) return null;
              return (
                <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="onboarding-complete-card">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center shrink-0"><CheckCircle2 className="w-6 h-6 text-emerald-400" /></div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold text-emerald-300">You're all set — you're live for riders! 🎉</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">Your profile is complete and discoverable on the Velopaths rider map. Take a look at how riders see you.</p>
                  </div>
                  <div className="flex gap-2 shrink-0 w-full sm:w-auto">
                    <Button onClick={() => setTab("contentprofile")} data-testid="onboarding-view-listing" className="flex-1 sm:flex-none bg-emerald-500 hover:bg-emerald-600 text-white"><Store className="w-4 h-4 mr-2" />Preview my listing</Button>
                    <Button variant="ghost" size="icon" onClick={() => { if (onboardKey) localStorage.setItem(onboardKey, "1"); setOnboardDoneDismissed(true); }} data-testid="onboarding-dismiss-complete" className="text-emerald-300"><X className="w-4 h-4" /></Button>
                  </div>
                </div>
              );
            }
            const nextStep = steps.find((s) => !s.done);
            return (
              <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6" data-testid="onboarding-checklist">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div>
                    <h3 className="font-heading text-lg font-bold">{profile?.partner_type === "trail" ? "Finish setting up your Trail Partner profile" : "Finish setting up your partner profile"}</h3>
                    <p className="text-sm text-muted-foreground">{profile?.partner_type === "trail" ? "Add your local knowledge — categories and stops riders will love. Complete these to go live." : "Complete these steps to go live for riders."}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-mono text-sm text-primary">{doneCount}/{steps.length}</span>
                    {nextStep && <Button size="sm" onClick={() => (nextStep.route ? navigate(nextStep.route) : setTab(nextStep.target))} data-testid="checklist-continue">Continue<ArrowRight className="w-4 h-4 ml-1.5" /></Button>}
                  </div>
                </div>
                <div className="space-y-2">
                  {steps.map((s) => {
                    const isNext = s === nextStep;
                    return (
                      <button
                        key={s.label}
                        onClick={() => (s.route ? navigate(s.route) : setTab(s.target))}
                        data-testid={`checklist-step-${s.testid || s.target}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${s.done ? "border-border bg-card/50" : isNext ? "border-primary bg-primary/10 ring-1 ring-primary/40" : "border-border bg-card hover:border-primary/50"}`}
                      >
                        {s.done ? <CheckCircle2 className="w-5 h-5 text-primary shrink-0" /> : <Circle className={`w-5 h-5 shrink-0 ${isNext ? "text-primary" : "text-muted-foreground"}`} />}
                        <span className={`flex-1 text-sm ${s.done ? "line-through text-muted-foreground" : "font-medium"}`}>{s.label}</span>
                        {isNext && <Badge className="bg-primary/20 text-primary border border-primary/30 text-xs">Next up</Badge>}
                        {!s.done && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <FollowUpsDueCard onOpenProspects={() => { setProspectsDue(true); setTab("prospects"); }} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Inbox} label="Pending approvals" value={stats.pending_approvals} color="text-amber-400" />
            <Stat icon={Users} label="Active advertisers" value={stats.active_advertisers} />
            <Stat icon={MapPin} label="Attractions" value={stats.attractions} color="text-cyan-400" />
            <Stat icon={Tags} label="Categories" value={stats.categories} color="text-orange-400" />
            <div className="sm:col-span-2 lg:col-span-4 space-y-4">
              <div className="rounded-2xl border border-border bg-card p-6" data-testid="shop-revenue-section">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-lg font-bold">Your revenue</h3>
                  {stats.recoup_achieved ? (
                    <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="recoup-achieved-badge">Setup fee recouped</Badge>
                  ) : (
                    <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid="recoup-pending-badge">${Math.max(0, 99 - (stats.shop_share || 0)).toFixed(2)} to recoup setup fee</Badge>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div><div className="text-xs text-muted-foreground mb-1">Total advertiser revenue</div><div className="text-2xl font-heading font-extrabold" data-testid="shop-gross-revenue">${(stats.gross_revenue || 0).toFixed(2)}</div></div>
                  <div><div className="text-xs text-muted-foreground mb-1">Your 50% share</div><div className="text-2xl font-heading font-extrabold text-primary" data-testid="shop-total-earned">${(stats.shop_share ?? stats.total_earned ?? 0).toFixed(2)}</div></div>
                  <div><div className="text-xs text-muted-foreground mb-1">Estimated annual earnings</div><div className="text-2xl font-heading font-extrabold" data-testid="shop-estimated-annual">${(stats.estimated_annual || 0).toFixed(2)}</div><div className="text-[11px] text-muted-foreground">{stats.active_advertisers} active × $49.50</div></div>
                  <div><div className="text-xs text-muted-foreground mb-1">Est. yearly riders</div><div className="text-2xl font-heading font-extrabold" data-testid="shop-rider-count-stat">{profile?.estimated_yearly_riders ?? "—"}</div></div>
                </div>
              </div>
              <RevenueShareNote />
              <div className="rounded-2xl border border-border bg-card p-6" data-testid="per-advertiser-breakdown">
                <h3 className="font-heading text-lg font-bold mb-1">Earnings by advertiser</h3>
                <p className="text-sm text-muted-foreground mb-4">Each active advertiser contributes $49.50/year to your shop (50% of their $99 Founding Advertiser annual fee).</p>
                {advertisers.filter((a) => a.subscription_status === "active").length === 0 ? (
                  <div className="text-sm text-muted-foreground">No active advertisers yet. Approve an advertiser's listing to start earning.</div>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Advertiser</TableHead><TableHead>Annual fee</TableHead><TableHead>Your share</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {advertisers.filter((a) => a.subscription_status === "active").map((a) => (
                        <TableRow key={a.id} data-testid={`earnings-row-${a.id}`}>
                          <TableCell className="font-medium">{a.business_name}</TableCell>
                          <TableCell>$99.00</TableCell>
                          <TableCell className="font-bold text-primary">$49.50</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
              <SetupFeeNote fee={stats.setup_fee ?? 99} />
            </div>
          </div>
        </div>
      )}

      {tab === "myzones" && <MyZonesPanel />}
      {tab === "prospects" && <ProspectsPanel followUpDue={prospectsDue} key={prospectsDue ? "due" : "all"} />}

      {tab === "marketplace" && (
        <div className="space-y-4">
          {(needsClaim || claimInReview) && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="marketplace-claim-notice">
              <Gift className="w-5 h-5 text-emerald-400 shrink-0" />
              <p className="text-sm text-muted-foreground flex-1">{needsClaim ? "Looking for your free zone? Your complimentary Founding Partner zone is claimed on a separate page — this Browse Zones tab is for leasing additional paid zones." : "Your complimentary zone claim is in review. This Browse Zones tab is for leasing additional paid zones."}</p>
              <Button size="sm" onClick={() => navigate("/claim-zone")} data-testid="marketplace-claim-cta" className="shrink-0 w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white">{needsClaim ? "Claim my free zone" : "View claim status"}</Button>
            </div>
          )}
          <MarketplacePanel />
        </div>
      )}

      {tab === "previewqr" && <ShopPreviewQR />}

      {tab === "receipt" && <ShopReceipt />}

      {tab === "billing" && <ShopBillingPanel />}

      {tab === "content" && <ContentManager mode="local" />}
      {tab === "contentprofile" && <ShopContentProfile />}
      {tab === "academy" && <PartnerAcademy />}
      {tab === "marketing" && <MarketingAssets />}
      {tab === "trailheads" && <TrailheadsAdminPanel mode="partner" />}

      {tab === "routes" && <ShopRoutesPanel />}

      {tab === "approvals" && (
        <div className="space-y-4" data-testid="approval-queue">
          <RevenueShareNote />
          {pending.length === 0 && <div className="text-muted-foreground text-sm">No listings awaiting approval.</div>}
          {pending.map((l) => {
            const adv = advertisers.find((a) => a.id === l.advertiser_id);
            const zonePolys = zones.map((z) => z.polygon).filter(Boolean);
            const zoneBoundary = zones[0]?.bike_path_boundary || [];
            const hasLoc = adv?.latitude != null && adv?.longitude != null;
            return (
            <div key={l.id} className="rounded-2xl border border-border bg-card p-5 space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-32 h-24 rounded-lg overflow-hidden bg-secondary shrink-0">
                  {l.photos?.[0] ? <img src={fileUrl(l.photos[0])} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><MapPin className="w-6 h-6" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold">{l.business_name}</div>
                  <div className="text-xs text-muted-foreground mb-1">{l.business_type} · {l.category_name}</div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{l.description}</p>
                </div>
                <div className="flex sm:flex-col gap-2 justify-end">
                  <Button size="sm" onClick={() => approve(l.id)} data-testid={`approve-btn-${l.id}`}><Check className="w-4 h-4 mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" onClick={() => reject(l.id)} data-testid={`reject-btn-${l.id}`}><X className="w-4 h-4 mr-1" />Reject</Button>
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{hasLoc ? "Sponsored pin location — this is where the pin appears for riders" : "This advertiser hasn't set a map location yet"}</div>
                {hasLoc && <ZonePinPreview lat={adv.latitude} lng={adv.longitude} zones={zonePolys} boundary={zoneBoundary} height={200} />}
              </div>
            </div>
          );})}
        </div>
      )}

      {tab === "advertisers" && (
        <div className="space-y-4">
          <RevenueShareNote />
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Business</TableHead><TableHead>Subscription</TableHead><TableHead>Listing</TableHead></TableRow></TableHeader>
            <TableBody>
              {advertisers.length === 0 && <TableRow><TableCell colSpan={3} className="text-muted-foreground">No advertisers yet.</TableCell></TableRow>}
              {advertisers.map((a) => (
                <TableRow key={a.id} data-testid={`advertiser-row-${a.id}`}>
                  <TableCell className="font-medium">{a.business_name}</TableCell>
                  <TableCell><Badge className={a.subscription_status === "active" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-slate-500/15 text-slate-300 border border-slate-500/30"}>{a.subscription_status}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{a.listing_status}</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </div>
      )}

      {tab === "categories" && (
        <div className="max-w-lg space-y-4">
          <div className="flex gap-2">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. Coffee, Scenic, Quick Bites" data-testid="category-name-input" onKeyDown={(e) => e.key === "Enter" && addCat()} />
            <Button onClick={addCat} data-testid="add-category-button"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="rounded-2xl border border-border bg-card divide-y divide-border" data-testid="categories-list">
            {categories.length === 0 && <div className="p-4 text-sm text-muted-foreground">No categories yet.</div>}
            {categories.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3" data-testid={`category-row-${c.id}`}>
                <span className="font-medium">{c.name}</span>
                <Button variant="ghost" size="sm" onClick={() => delCat(c.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "payouts" && (
        <>
          {connect && !connect.payouts_enabled && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5 mb-4" data-testid="connect-onboarding-banner">
              <div className="flex items-start gap-3">
                <TriangleAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-heading font-bold">Set up payouts to get paid</h4>
                  <p className="text-sm text-muted-foreground mt-1">To receive your 50% share of advertiser payments, you'll set up payouts through Stripe, our payments provider. Clicking below opens Stripe's secure form where <strong>you</strong> enter your business/ID details and bank account — about 5 minutes. When you finish, you'll come straight back here and payouts turn on automatically. (Stripe won't contact you — it's a form you complete now.) Until then, your share is held as "pending".</p>
                  <Button className="mt-3" onClick={startConnectOnboarding} disabled={connectLoading} data-testid="start-connect-onboarding">
                    {connectLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{connect.details_submitted ? "Continue payout setup" : "Set up payouts"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {connect && connect.payouts_enabled && (
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 mb-4 flex items-center gap-3" data-testid="connect-enabled-banner">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
              <p className="text-sm">Payouts are set up — your 50% share is transferred automatically after each advertiser payment.</p>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <Table data-testid="payout-history-table">
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Gross</TableHead><TableHead>Share %</TableHead><TableHead>Your payout</TableHead><TableHead>Method</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {payouts.length === 0 && <TableRow><TableCell colSpan={6} className="text-muted-foreground">No payouts yet. They appear when advertisers pay.</TableCell></TableRow>}
                {payouts.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{new Date(p.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>${p.gross_amount?.toFixed(2)}</TableCell>
                    <TableCell>{p.revenue_share_percent}%</TableCell>
                    <TableCell className="font-bold text-primary">${p.amount?.toFixed(2)}</TableCell>
                    <TableCell>{p.method}</TableCell>
                    <TableCell><Badge className={p.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border border-amber-500/30"}>{p.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      {tab === "settings" && profile && (
        <div className="max-w-lg space-y-4 rounded-2xl border border-border bg-card p-6">
          <div>
            <Label className="mb-2 block">Shop logo</Label>
            <PhotoUpload photos={profile.logo_file_id ? [profile.logo_file_id] : []} onChange={(ids) => setProfile({ ...profile, logo_file_id: ids[0] || null })} multiple={false} label="Logo" testId="shop-logo-upload" />
            <p className="text-xs text-muted-foreground mt-1">Shown on your dashboard and on every Sponsored Pin.</p>
          </div>
          <div><Label>Shop name</Label><Input value={profile.name || ""} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name-input" className="mt-1.5" /></div>
          <div><Label>Region</Label><Input value={profile.region || ""} onChange={(e) => setProfile({ ...profile, region: e.target.value })} data-testid="profile-region-input" className="mt-1.5" /></div>
          <div><Label>Address</Label><Input value={profile.address || ""} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="123 Thames St, Bristol, RI 02809" data-testid="profile-address-input" className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Phone</Label><Input value={profile.phone || ""} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="(401) 555-0100" data-testid="profile-phone-input" className="mt-1.5" /></div>
            <div><Label>Hours</Label><Input value={profile.hours || ""} onChange={(e) => setProfile({ ...profile, hours: e.target.value })} placeholder="Mon–Sat 9–6" data-testid="profile-hours-input" className="mt-1.5" /></div>
          </div>
          <div><Label>Description</Label><Textarea value={profile.description || ""} onChange={(e) => setProfile({ ...profile, description: e.target.value })} rows={3} placeholder="Tell riders about your shop, rentals, and services." data-testid="profile-description-input" className="mt-1.5" /></div>
          <div><Label>Rental booking URL</Label><Input type="url" value={profile.booking_url || ""} onChange={(e) => setProfile({ ...profile, booking_url: e.target.value })} placeholder="https://yourshop.com/book" data-testid="profile-booking-url-input" className="mt-1.5" /><p className="text-xs text-muted-foreground mt-1">Adds a "Book a Rental" button to your shop pin on the Rider app map.</p></div>
          <div>
            <Label className="mb-2 block">Shop photos</Label>
            <PhotoUpload photos={profile.photos || []} onChange={(photos) => setProfile({ ...profile, photos })} label="Photos" testId="shop-photos-upload" />
            <p className="text-xs text-muted-foreground mt-1">Shown in the rider app on your shop card.</p>
          </div>
          <div>
            <Label>Estimated yearly riders in your region</Label>
            <Input type="number" min="0" value={profile.estimated_yearly_riders ?? ""} onChange={(e) => setProfile({ ...profile, estimated_yearly_riders: e.target.value })} placeholder="4000" data-testid="shop-rider-count-input" className="mt-1.5" />
            <p className="text-xs text-muted-foreground mt-1">Pre-fills the cost-per-rider estimate for all your advertisers. They can still override it.</p>
          </div>
          <div>
            <Label>Street address</Label>
            <AddressAutocomplete value={profile.address || ""}
              onChange={(v) => setProfile({ ...profile, address: v })}
              onSelect={(s) => setProfile({ ...profile, address: s.address || profile.address, city: s.city || profile.city, state: s.state || profile.state, zip: s.zip || profile.zip, latitude: s.lat, longitude: s.lng })}
              placeholder="Start typing your address…" testId="profile-address-input" className="mt-1.5" />
            <p className="text-xs text-muted-foreground mt-1">Picking from the list keeps your rider-map pin exact.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><Label>City</Label><Input value={profile.city || ""} onChange={(e) => setProfile({ ...profile, city: e.target.value })} data-testid="profile-city-input" className="mt-1.5" /></div>
            <div><Label>State</Label><Input value={profile.state || ""} onChange={(e) => setProfile({ ...profile, state: e.target.value })} data-testid="profile-state-input" className="mt-1.5" /></div>
            <div><Label>ZIP code</Label><Input value={profile.zip || ""} onChange={(e) => setProfile({ ...profile, zip: e.target.value })} data-testid="profile-zip-input" className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Shop location (for the rider map)</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">Click the map or drag the pin to set your exact location. You can also type coordinates below.</p>
            <LocationPicker lat={profile.latitude} lng={profile.longitude} onChange={(lat, lng) => setProfile({ ...profile, latitude: lat, longitude: lng })} />
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div><Label>Latitude</Label><Input type="number" step="any" value={profile.latitude ?? ""} onChange={(e) => setProfile({ ...profile, latitude: e.target.value })} placeholder="41.6772" data-testid="profile-latitude-input" className="mt-1.5" /></div>
              <div><Label>Longitude</Label><Input type="number" step="any" value={profile.longitude ?? ""} onChange={(e) => setProfile({ ...profile, longitude: e.target.value })} placeholder="-71.2662" data-testid="profile-longitude-input" className="mt-1.5" /></div>
            </div>
          </div>
          <div><Label>Contact email</Label><Input value={profile.contact_email || ""} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })} className="mt-1.5" /></div>
          <div className="rounded-lg border border-border p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="payout-settings-note">
            <p className="text-sm text-muted-foreground">Payouts are handled securely through Stripe. Set up or manage your bank details on the Payouts tab.</p>
            <Button variant="outline" size="sm" onClick={() => setTab("payouts")} data-testid="settings-go-payouts" className="shrink-0 w-full sm:w-auto">Go to Payouts</Button>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <Label>Weekly digest email</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Get a Monday summary of pending approvals and active advertisers.</p>
            </div>
            <Switch checked={profile.digest_enabled !== false} onCheckedChange={(v) => setProfile({ ...profile, digest_enabled: v })} data-testid="digest-toggle" />
          </div>
          <Button variant="outline" onClick={sendDigestPreview} data-testid="send-digest-preview-button"><Mail className="w-4 h-4 mr-2" />Send me a test copy now</Button>
          <Button onClick={saveProfile} data-testid="save-profile-button">Save profile</Button>
        </div>
      )}
    </Shell>
  );
}
